import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import BackButton from '../../components/ui-components/BackButton'
import { goBack, trackEvent, updateAppStatusBar, disableHardwareBack, onReady } from '../../utils/appBridge'
import { getTopPadding } from '../../utils/sessionStorage'
import { throttle } from '../../utils/utils'
import EliteHeader from '../../components/regular-to-direct-plan/EliteHeader'
import ListOfCommisionFunds from '../../components/regular-to-direct-plan/ListOfCommisionFunds'
import ListOfUnderReviewFunds from '../../components/regular-to-direct-plan/ListOfUnderReviewFunds'
import Divider from '../../components/ui-components/Divider'
import RebalancingDisclaimer from '../../components/power-rebalancing/RebalancingDisclaimer'
import { fetchRegularToDirectSummary } from '../../repository/regular-to-direct-plan'
import { WithdrawnFunds } from '../../components/regular-to-direct-plan/WithdrawnFunds'
import { EventNames } from '../../types/misc.types'
import { RegularToDirectPlanFAQs } from '../../components/regular-to-direct-plan/RegularToDirectPlanFAQs'

export default function RegularToDirectPlanElitePage() {
  const [showSmallHeader, setShowSmallHeader] = useState(false)
  const headerHeight = `calc(${getTopPadding()}px + 54px)`

  const {
    data: summaryData,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['regularToDirectSummary'],
    queryFn: fetchRegularToDirectSummary,
    retry: 1,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: 'REGULAR_TO_DIRECT_PLAN_ELITE_PAGE'
      }
    })
    updateAppStatusBar('FFFFFF', 'dark')
    disableHardwareBack()
  }, [])

  useEffect(() => {
    if (!isLoading && !isError && summaryData) {
      onReady()
    }
  }, [isLoading, isError, summaryData])

  const throttledScroll = throttle((scrollPosition: number) => {
    setShowSmallHeader(scrollPosition >= 70)
  }, 0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollPosition = e.currentTarget.scrollTop || 0
    throttledScroll(scrollPosition)
  }

  return (
    <div className='h-[100vh] w-full flex flex-col overflow-hidden'>
      <div
        style={{ height: headerHeight }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99] bg-white flex-shrink-0'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b ${showSmallHeader ? 'border-[#EBF2F2]' : 'border-white'} `}
        >
          <div className='flex absolute z-1'>
            <BackButton size={24} onClick={() => goBack()} />
          </div>
          <div className='w-full flex justify-start ml-10 items-center'>
            {showSmallHeader && (
              <div className='max-w-[70%] animate-fade-150 truncate font-[500] mr-4'>Regular to Direct Plan</div>
            )}
          </div>
        </div>
      </div>
      <div
        style={{ height: `calc(100vh - ${headerHeight})` }}
        onScroll={handleScroll}
        className='w-full flex flex-col flex-1 overflow-y-scroll no-scrollbar overflow-x-hidden overscroll-none'
      >
        <EliteHeader regularFundCount={summaryData?.regularFundCount || 0} />
        <ListOfCommisionFunds summaryData={summaryData} isLoading={isLoading} isError={isError} />
        {summaryData?.regularFunds?.some((fund) => fund.exited && !fund.directSwitchRecommended) &&
          summaryData?.regularFunds?.some((fund) => !fund.exited && !fund.directSwitchRecommended) && (
            <Divider className="mt-4 mb-9" />
          )}
        <ListOfUnderReviewFunds summaryData={summaryData} isLoading={isLoading} isError={isError} />
        <Divider className='mb-0 mt-3' />
        <WithdrawnFunds summaryData={summaryData} isLoading={isLoading} isError={isError} />
        <RegularToDirectPlanFAQs screenType='REGULAR_TO_DIRECT_PLAN_ELITE_PAGE' />
        <RebalancingDisclaimer />
      </div>
    </div>
  )
}
